import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {isOk} from '../../../utils/apiResponse';
import {Spinner} from '../../Spinner';
import {WebhookApi} from '../../../api/alertRulesApi';
import {HELP_WEBHOOK, LinkToArticle} from '../../help_center/LinkToArticle';
import {formatDate} from '../../agency/v2/AlertsPopover';
import {systemRuleTitle} from '../ruleText';
import {ALERT_RULE_EVENT} from '../systemRulesModel';
import {deliveriesState, eventLabelKey, eventsForSave, groupEvents, sameSelection, testOutcome} from './webhookModel';
import {WEBHOOK_ERROR, WEBHOOK_LOADING, WEBHOOK_NO_MODULE, WEBHOOK_OK} from './ChannelsCard';

/**
 * «Avisos en tu chat o gestor de tareas»: el webhook de la agencia en la 2.1, al pie de «Mis reglas»
 * (ancla `#webhook`). Antes vivía en Conexiones › Plataformas; ese frente cambia su tarjeta por un
 * link acá cuando esto esté en producción (D11). El `WebhookComponent` clásico no se toca: los dos
 * editan la misma URL y los mismos eventos contra los mismos endpoints.
 *
 * Lo que agrega sobre el clásico:
 * - Los eventos en dos grupos, y en «Alertas» cada uno con el nombre de la regla que lo dispara.
 * - «Probar» honesto: con `data` del backend dice si llegó y qué contestó el destino; sin él, sólo que
 *   se envió (el test viejo decía OK aunque la URL respondiera 404).
 * - El estado de los últimos envíos (`deliveries`), si el backend lo manda.
 * - Sin el módulo, la tarjeta se ve deshabilitada con el plan que lo incluye (el clásico dejaba los
 *   controles habilitados).
 * - Errores con `toast`, nunca `alert()`.
 */
// «Antes estaba en Conexiones» es verdad recién cuando Plataformas saque su tarjeta del webhook (D11):
// hasta entonces las dos conviven y la etiqueta mentía (C-ac-fe-centro-20). Ese frente lo prende.
export const WEBHOOK_MOVED_FROM_CONNECTIONS = false;

/**
 * `onlyChanges` (Centro de Alertas v2, README v2 §4.5): con las alertas convertidas, la URL se carga en el
 * panel de 3 pasos y cada alerta elige su webhook en su fila, así que esta tarjeta queda plegada en
 * «Avanzado» sólo con los eventos que no son alertas («Cambios en tus cuentas»). Guarda la URL de siempre y
 * manda los eventos de alertas como los dejan las filas AHORA (`forcedEvents`: los de las filas con el webhook
 * prendido), no la foto que se leyó al cargar: esa foto apagaba el evento que una fila prendió después (review
 * C-av2-fe-centro-03). `sectionId`: el ancla `#webhook` es de la tarjeta de estado en ese modo.
 */
export function WebhookCardV2({webhook, systemRules, onSaved, onRetry, readOnly = false, onlyChanges = false,
                                  sectionId = 'webhook', hiddenEvents = [], forcedEvents = []}) {
    const {t, i18n} = useTranslation();
    const [url, setUrl] = useState('');
    const [selected, setSelected] = useState([]);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [test, setTest] = useState(null);

    const titleId = sectionId === 'webhook' ? 'alc-webhook-title' : `${sectionId}-title`;
    const status = webhook ? webhook.status : WEBHOOK_LOADING;
    const noModule = status === WEBHOOK_NO_MODULE;
    const locked = noModule || readOnly;
    const savedUrl = (webhook && webhook.url) || '';
    const savedSelected = useMemo(() => (webhook && webhook.selected) || [], [webhook]);

    // Lo guardado manda al llegar y después de guardar: lo tipeado sin guardar se pierde sólo si
    // cambia lo de abajo (otra pestaña, una recarga).
    useEffect(() => {
        setUrl(savedUrl);
        setSelected(savedSelected);
    }, [savedUrl, savedSelected]);

    // `hiddenEvents`: los que ya gobierna el interruptor Webhook de una fila (los avisos de control, v2). No se
    // muestran, y guardar los deja como estaban.
    const hiddenKey = hiddenEvents.join(',');
    const groups = useMemo(() => {
        const grouped = groupEvents(webhook ? webhook.events : [], systemRules);
        const hidden = new Set(hiddenKey ? hiddenKey.split(',') : []);
        return {...grouped, changes: grouped.changes.filter((event) => !hidden.has(event.id))};
    }, [webhook, systemRules, hiddenKey]);
    const allIds = useMemo(() => [...groups.alerts, ...groups.changes].map((event) => event.id), [groups]);
    const changeIds = useMemo(() => groups.changes.map((event) => event.id), [groups]);
    const dirty = url.trim() !== savedUrl.trim() || !sameSelection(selected, savedSelected);
    const delivery = status === WEBHOOK_OK ? deliveriesState(webhook.deliveries) : null;
    // Sin URL, los cambios no tienen adónde ir: se conecta primero (la tarjeta de arriba).
    const changesLocked = onlyChanges && !savedUrl;

    const toggle = (id) => setSelected((current) => (current.includes(id)
        ? current.filter((item) => item !== id) : [...current, id]));
    // «Todos» y «Ninguno» de «Avanzado» tocan sólo los cambios: los eventos de alertas los deciden las filas.
    const selectAll = () => setSelected((current) => (onlyChanges
        ? [...new Set([...current, ...changeIds])] : allIds));
    const selectNone = () => setSelected((current) => (onlyChanges
        ? current.filter((id) => !changeIds.includes(id)) : []));

    const eventName = (event) => {
        const key = eventLabelKey(event.id);
        const text = t(key);
        // Un evento nuevo del backend sin clave propia: un texto genérico con su id, no el nombre que
        // manda el backend (sólo existe en español) (C-ac-fe-centro-25).
        return text === key ? t('alerts_center.webhook.event_other', {id: event.id}) : text;
    };

    const triggeredBy = (event) => {
        if (event.id === ALERT_RULE_EVENT) return t('alerts_center.webhook.triggered_by_custom');
        const names = [...new Set((event.triggeredBy || []).map((rule) => {
            const {title, subtitle} = systemRuleTitle(rule, t);
            return subtitle ? `${title} (${subtitle})` : title;
        }))];
        return names.length ? t('alerts_center.webhook.triggered_by', {
            names: names.join(' · '), interpolation: {escapeValue: false},
        }) : null;
    };

    const save = () => {
        if (!dirty || saving || locked) return;
        setSaving(true);
        const events = onlyChanges ? eventsForSave(selected, {governed: hiddenEvents, on: forcedEvents}) : selected;
        WebhookApi.save({webhookUrl: url.trim(), events}).then((response) => {
            setSaving(false);
            if (!isOk(response)) {
                toast.error(t('alerts_center.webhook.save_error'));
                return;
            }
            toast.success(t('alerts_center.webhook.saved'));
            onSaved({...webhook, url: url.trim(), selected: [...events]});
        });
    };

    const runTest = () => {
        if (!url.trim() || testing) return;
        setTesting(true);
        setTest(null);
        WebhookApi.test({webhookUrl: url.trim(), events: selected}).then((response) => {
            setTesting(false);
            setTest(testOutcome(response));
        });
    };

    const renderEvents = (title, events, withTriggers) => (
        <div className="alc-webhook__group">
            <h3 className="alc-webhook__group-title">{title}</h3>
            <ul className="alc-webhook__events">
                {events.map((event) => {
                    const by = withTriggers ? triggeredBy(event) : null;
                    const inputId = `alc-event-${event.id}`;
                    return (
                        <li key={event.id} className="alc-webhook__event">
                            <input id={inputId} type="checkbox" checked={selected.includes(event.id)}
                                   onChange={() => toggle(event.id)}/>
                            <label htmlFor={inputId}>
                                <span className="alc-webhook__event-name">{eventName(event)}</span>
                                {by && <span className="alc-webhook__event-by">{by}</span>}
                            </label>
                        </li>
                    );
                })}
            </ul>
        </div>
    );

    const renderDelivery = () => {
        if (!delivery || !savedUrl) return null;
        if (delivery.state === 'failing') {
            const since = formatDate(delivery.since, i18n.language);
            return (
                <p className="alc-webhook__delivery -warn" data-state="failing">
                    {t(delivery.reasonKey, {code: delivery.code || '—'})}
                    {' '}
                    {since
                        ? t('alerts_center.webhook.failing_since', {since})
                        : t('alerts_center.webhook.failing_always')}
                </p>
            );
        }
        if (delivery.state === 'idle') {
            return <p className="alc-webhook__delivery" data-state="idle">{t('alerts_center.webhook.delivery_idle')}</p>;
        }
        return (
            <p className="alc-webhook__delivery -ok" data-state="ok">
                {t('alerts_center.webhook.delivery_ok', {count: delivery.sent})}
                {delivery.failed > 0 && ` ${t('alerts_center.webhook.delivery_failed', {count: delivery.failed})}`}
            </p>
        );
    };

    const renderBody = () => {
        if (status === WEBHOOK_LOADING) return <div className="box-loader"><Spinner color="positive small"/></div>;
        if (status === WEBHOOK_ERROR) {
            return (
                <p className="alc-notice -warn">
                    {t('alerts_center.webhook.load_error')}
                    {' '}
                    <button type="button" className="alc-link -button" onClick={onRetry}>
                        {t('alerts_center.retry')}
                    </button>
                </p>
            );
        }
        return (
            <>
                {noModule && <p className="alc-notice" data-testid="alc-webhook-no-module">{t('alerts_center.webhook.no_module')}</p>}
                {changesLocked && !noModule && (
                    <p className="alc-notice" data-testid="alc-webhook-changes-locked">{t('alerts_center.webhook.changes_need_url')}</p>
                )}
                <fieldset className="alc-webhook__fields" disabled={locked || changesLocked}>
                    {!onlyChanges && (
                        <>
                            <label htmlFor="alc-webhook-url" className="alc-webhook__label">{t('alerts_center.webhook.url_label')}</label>
                            <div className="alc-webhook__url">
                                <input id="alc-webhook-url" type="url" value={url} placeholder="https://"
                                       onChange={(event) => setUrl(event.target.value)}/>
                                <button type="button" className="alc-btn -ghost" disabled={!url.trim() || testing}
                                        onClick={runTest}>
                                    {testing ? t('alerts_center.webhook.testing') : t('alerts_center.webhook.test')}
                                </button>
                            </div>
                            {test && (
                                <p className={`alc-webhook__test -${test.tone}`} role="status" data-testid="alc-webhook-test">
                                    {t(test.key, {code: test.code || '—'})}
                                </p>
                            )}
                            {renderDelivery()}
                        </>
                    )}
                    <div className="alc-webhook__bulk">
                        <button type="button" className="alc-link -button" onClick={selectAll}>
                            {t('alerts_center.webhook.select_all')}
                        </button>
                        <button type="button" className="alc-link -button" onClick={selectNone}>
                            {t('alerts_center.webhook.select_none')}
                        </button>
                    </div>
                    {!onlyChanges && groups.alerts.length > 0 && renderEvents(t('alerts_center.webhook.group_alerts'), groups.alerts, true)}
                    {groups.changes.length > 0 && renderEvents(t('alerts_center.webhook.group_changes'), groups.changes, false)}
                </fieldset>
                <div className="alc-card__foot">
                    <button type="button" className="alc-btn -primary" disabled={locked || changesLocked || !dirty || saving}
                            onClick={save}>
                        {saving ? t('alerts_center.saving') : t('alerts_center.webhook.save')}
                    </button>
                </div>
            </>
        );
    };

    return (
        <section id={sectionId} tabIndex={-1} className={`alc-card alc-webhook${noModule ? ' -disabled' : ''}`}
                 aria-labelledby={titleId} data-testid="alc-webhook">
            <header className="alc-card__head">
                <h2 id={titleId} className="alc-card__title">
                    {t(onlyChanges ? 'alerts_center.webhook.changes_title' : 'alerts_center.webhook.title')}
                </h2>
                {WEBHOOK_MOVED_FROM_CONNECTIONS && <span className="alc-tag">{t('alerts_center.webhook.moved_tag')}</span>}
                <span className="alc-card__spacer"/>
                <LinkToArticle article={HELP_WEBHOOK} plain/>
            </header>
            <p className="alc-card__lede">{t(onlyChanges ? 'alerts_center.webhook.changes_lede' : 'alerts_center.webhook.lede')}</p>
            {renderBody()}
        </section>
    );
}
